import { memo } from 'react'
import type { Representante } from '../api'
import { moeda } from '../formato'
import { MEDALHA, classeLinhaRanking, nomesDoRepresentante } from '../ranking'

/**
 * Ranking de representante da divisão — o que o card mostra desde 13/10, no
 * lugar do feed de pedidos.
 *
 * **É o oposto do `FeedPedidos`, e de propósito.** Lá a exigência é que a lista
 * NÃO seja redesenhada: pedido chega no topo, os nós que estavam ficam. Aqui o
 * conteúdo é a ORDEM — quem passa do 4º para o 3º lugar troca de linha e ganha
 * medalha, e essa troca é justamente o que a sala quer ver. Por isso:
 *
 *  1. a `key` é o CÓDIGO do representante, não a posição. Com a posição na
 *     chave, o React reusaria o nó da 3ª linha para outra pessoa e a transição
 *     de cor da medalha aconteceria no lugar errado;
 *  2. `memo` na linha comparada pela identidade, como no feed: num ciclo em que
 *     só o 1º lugar mudou de valor, as outras 14 linhas não repintam;
 *  3. a posição vem do SERVIDOR (`representante.posicao`), não do índice do
 *     array. Lista que chegue pela metade mostraria "1º" para quem é 7º.
 *
 * Quatro colunas, nesta ordem (decisão de 13/10): razão social, fantasia, nome
 * da pessoa e o valor total. Cada nome pode faltar no cadastro do ERP, e o que
 * falta não vira linha vazia — `nomesDoRepresentante` resolve o que exibir.
 *
 * **A lista é COMPLETA e ROLA desde 16/10.** Eram os 15 maiores, com o resto
 * recortado por `overflow: hidden`; agora vêm os 40 a 60 representantes da
 * marca e a `.lista-ranking` rola (barra, roda do mouse, ou teclado depois de
 * um clique nela). Nada rola sozinho: no telão a lista fica parada onde está, e
 * quem quer ver o fim rola na mão.
 */

type PropsLinha = { representante: Representante }

function LinhaRankingBase({ representante }: PropsLinha) {
  const { principal, fantasia, pessoal } = nomesDoRepresentante(representante)
  const medalha = MEDALHA[representante.posicao]

  return (
    <div className={classeLinhaRanking(representante.posicao)} role="listitem">
      {/* A medalha é o pódio; da 4ª posição em diante fica só o número. O
          `aria-label` existe porque emoji sem rótulo é lido como "imagem". */}
      <span className="rep-posicao">
        {medalha ? (
          <span className="rep-medalha" role="img" aria-label={medalha.rotulo}>
            {medalha.emoji}
          </span>
        ) : (
          <span className="rep-numero">{representante.posicao}</span>
        )}
      </span>
      {/* `title` com os três nomes: no telão o texto é recortado por largura, e
          quem chega perto da tela consegue ler o resto. */}
      <span className="rep-nome" title={principal ?? undefined}>{principal}</span>
      <span className="rep-fantasia" title={fantasia ?? undefined}>{fantasia}</span>
      <span className="rep-pessoal" title={pessoal ?? undefined}>{pessoal}</span>
      <span className="rep-valor">{moeda(representante.valor)}</span>
    </div>
  )
}

const LinhaRanking = memo(
  LinhaRankingBase,
  (antes, depois) => antes.representante === depois.representante,
)

type Props = { representantes: Representante[] }

function RankingRepresentantesBase({ representantes }: Props) {
  /* `.painel-feed` junto: o lado direito da visão de marca é o MESMO painel (a
     grade de `.visao-marca` tem duas colunas), e herdar o layout dele é o que
     mantém as duas telas com a mesma moldura. `.painel-ranking` é só o que
     difere. */
  return (
    <div className="painel-feed painel-ranking">
      <div className="titulo-feed">
        <span>Representantes</span>
        {/* A CONTAGEM, não mais `top 15`: a lista vai completa desde 16/10, e
            "top" dizia que havia gente de fora. O número sozinho é o que cabe
            no espaço; o plural fica no `title`, para quem chega perto. */}
        <span
          className="contagem-pedidos"
          title={
            representantes.length === 0
              ? 'Nenhum representante nesta divisão ainda hoje'
              : `${representantes.length} representantes`
          }
        >
          {representantes.length === 0 ? '—' : representantes.length}
        </span>
      </div>

      {/* O cabeçalho usa a MESMA grade das linhas, para as colunas alinharem. */}
      <div className="cabecalho-ranking">
        <span />
        <span>Representante</span>
        <span>Fantasia</span>
        <span>Nome</span>
        <span>Total</span>
      </div>

      {representantes.length === 0 ? (
        /* Lista vazia é MEDIÇÃO ("ninguém vendeu nesta divisão ainda"), e dizer
           isso por escrito evita a leitura de "faltou carregar" — que é o que
           uma área em branco no telão comunica. */
        <div className="sem-pedido">Nenhum pedido nesta divisão ainda hoje.</div>
      ) : (
        /* `tabIndex` numa div que não é controle existe por um motivo: a lista
           ROLA, e um container rolável sem foco não responde a `↑ ↓ PageUp
           PageDown` — quem não usa roda de mouse não alcançaria da 16ª posição
           para baixo. As teclas da rotação não disputam: `usarRotacao` captura
           `Space`, `←` e `→` com `preventDefault`, e não captura as verticais. */
        <div
          className="lista-ranking"
          role="list"
          tabIndex={0}
          aria-label="Ranking de representantes"
        >
          {representantes.map(representante => (
            <LinhaRanking
              key={representante.codigo ?? `posicao-${representante.posicao}`}
              representante={representante}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* `memo` no ranking inteiro: a visão da divisão repinta a cada segundo (o
   relógio do cabeçalho) e sem isto a lista inteira seria reconciliada junto —
   o que passou a pesar quando ela deixou de ter 15 linhas e passou a ter as
   40 a 60 da marca. */
export const RankingRepresentantes = memo(RankingRepresentantesBase)
